import { React, useState, useEffect, useRef } from "react";
import { Link, useNavigate, NavLink, useLocation } from "react-router-dom";
import "./nav-style.css";
import GoogleLoginDialog from "../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../dialog/confirm/confirm-dialog";
import AuthService from "../../service/auth-service";
import ConsultService from "../../service/consult-service";
import UserService from "../../service/user-service";
import useIsMobile from "../../hooks/useIsMobile";

const NavListComponent = ({
  showServiceMenu,
  setShowServiceMenu,
  showPersonalMenu,
  setShowPersonalMenu,
}) => {
  const isMobile = useIsMobile();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  //「個人專屬命盤」gating 用的「前往註冊」確認框
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  //諮詢未讀驚嘆號：問問老師("0")串 / 我的書房任一報告 有客服未讀回覆
  const [unreadAsk, setUnreadAsk] = useState(false);
  const [unreadStudy, setUnreadStudy] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [user, setUser] = useState(AuthService.getUserProfile());
  const [isLogin, setIsLogin] = useState(AuthService.getLoginStatus());
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  //帶給 /register 的「來源頁」：完成註冊後使用者會回到這個頁面
  //  特例：來源就在 /register 自己 / 登入相關頁 → 沒意義，fallback 為 "/"
  const registerFrom =
    location.pathname && location.pathname !== "/register"
      ? location.pathname
      : "/";
  //點「個人專屬命盤」時的意圖旗標：登入/註冊完成後要不要把使用者送進 /personal-chart
  const personalChartIntentRef = useRef(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  //處理登入成功 callback
  //  dialog 內已透過 Firebase popup 拿到 idToken，這裡只負責換自家 cookie
  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;

    try {
      const response = await AuthService.firebaseLogin(idToken);
      if (response.data.status) {
        AuthService.login(response.data.data);
        setUser(AuthService.getUserProfile());
        setShowLoginDialog(false);
        setIsLogin(true);

        const validCode = response.data.data.userValidCode;
        if (validCode !== "2") {
          //未完成註冊 → 去註冊；若是為了看命盤而登入，註冊完回命盤，否則回原頁
          const from = personalChartIntentRef.current
            ? "/personal-chart"
            : registerFrom;
          navigate("/register", { state: { from } });
        } else if (personalChartIntentRef.current) {
          //已完成註冊 + 本來就要看命盤 → 直接進命盤
          navigate("/personal-chart");
        }
        personalChartIntentRef.current = false;
      }
    } catch (e) {
      console.error("[Nav] firebase-login 失敗:", e);
    }
  };

  //「個人專屬命盤」: 點擊先做雙層 gating，通過(validCode==="2")才真的進命盤
  //  未登入 → 跳登入框；已登入但未完成註冊 → 跳「前往註冊」確認框
  //  兩個 dialog 關閉都留在當前頁、不跳轉
  const handlePersonalChartClick = (e) => {
    if (e) e.preventDefault();
    const isLoginNow = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLoginNow || !profile) {
      personalChartIntentRef.current = true;
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      personalChartIntentRef.current = true;
      setShowRegisterPrompt(true);
      return;
    }
    navigate("/personal-chart");
  };

  //撈諮詢未讀狀態 → 控制「問問老師 / 我的書房」旁的驚嘆號
  //  未登入直接清掉；失敗靜默不擾使用者
  const refreshConsultUnread = async () => {
    if (!AuthService.getLoginStatus()) {
      setUnreadAsk(false);
      setUnreadStudy(false);
      return;
    }
    try {
      const res = await ConsultService.getUnread();
      if (res.data?.status) {
        setUnreadAsk(!!res.data.data.askTeacher);
        setUnreadStudy(!!res.data.data.study);
      }
    } catch (e) {
      //靜默：未讀只是輔助提示，失敗不影響 nav
    }
  };

  //刷新本地 profile（重點是 A-Point 餘額）→ 與點數管理一致
  //  諮詢扣點 / 購買加點都發生在 server，登入後快取的 profile 會過時 → 開選單時重撈
  const refreshUserProfile = async () => {
    if (!AuthService.getLoginStatus()) return;
    try {
      const res = await UserService.getPersonalInfo();
      if (res.data?.status && res.data.data) {
        const merged = { ...AuthService.getUserProfile(), ...res.data.data };
        AuthService.updateUserInfo(merged); //持久化 + 廣播 user-updated → nav 等同步刷新
      }
    } catch (e) {
      //靜默：餘額顯示失敗不擾使用者
    }
  };

  //進站預抓一次（已登入才會真的打）→ 第一次打開選單就即時顯示驚嘆號 / 餘額
  useEffect(() => {
    refreshConsultUnread();
    refreshUserProfile();
  }, []);

  //之後每次「打開個人選單」重撈：未讀驚嘆號 + A-Point 餘額都即時
  useEffect(() => {
    if (showPersonalMenu) {
      refreshConsultUnread();
      refreshUserProfile();
    }
  }, [showPersonalMenu]);

  //點登出 → 先跳確認框，確認後才真的登出
  const openLogoutConfirm = () => {
    setShowPersonalMenu(false);
    setShowLogoutConfirm(true);
  };

  //登出系統
  const handleLogout = () => {
    setShowPersonalMenu(false);
    setIsLogin(false);
    AuthService.logout();
    navigate("/");
  };

  useEffect(() => {
    if (AuthService.getLoginStatus()) {
      //登入狀態
      setUser(AuthService.getUserProfile());
      setIsLogin(true);

      if (AuthService.getUserProfile()?.userValidCode === "1") {
        navigate("/register", { state: { from: registerFrom } });
      }
    } else {
      //未登入：只更新 nav 自己的顯示狀態，不代全站決定路由
      //  之前這裡會 navigate("/") → 導致 F5 任何頁面都被拖回首頁
      //  真的需要登入才能用的動作（下訂單/解鎖）由該頁面自己彈 login dialog 擋
      setIsLogin(false);
    }
  }, []);

  //外部觸發登出（例：axios interceptor 收到 401 → main-layout AuthService.logout 後）→ 同步重置 nav 狀態
  useEffect(() => {
    const onAuthExpired = () => {
      setIsLogin(false);
      setUser(null);
      setShowPersonalMenu(false);
      setUnreadAsk(false);
      setUnreadStudy(false);
    };
    window.addEventListener("auth-expired", onAuthExpired);
    return () => window.removeEventListener("auth-expired", onAuthExpired);
  }, [setShowPersonalMenu]);

  //外部更新 user profile（例：個人檔案頁換大頭貼、或文章頁觸發登入）→ nav 同步刷新
  //  同時刷 isLogin（剛從非登入變登入的情境）
  useEffect(() => {
    const onUserUpdated = (e) => {
      setUser(e.detail || AuthService.getUserProfile());
      setIsLogin(AuthService.getLoginStatus());
    };
    window.addEventListener("user-updated", onUserUpdated);
    return () => window.removeEventListener("user-updated", onUserUpdated);
  }, []);

  return (
    <div className="nav-main">
      {/* Dialog 移到最外層，桌面/手機都能觸發 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
            personalChartIntentRef.current = false;
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 個人專屬命盤 gating：已登入但未完成註冊 → 確認去註冊；關閉留在當前頁 */}
      {showRegisterPrompt && (
        <ConfirmDialog
          open={showRegisterPrompt}
          onClose={() => {
            setShowRegisterPrompt(false);
            personalChartIntentRef.current = false;
          }}
          onSuccess={() => {
            setShowRegisterPrompt(false);
            personalChartIntentRef.current = false;
            navigate("/register", { state: { from: "/personal-chart" } });
          }}
          title="尚未完成註冊"
          message="個人專屬命盤需要完成註冊才能查看，<br>是否前往註冊頁完成？"
          confirmTxt="前往註冊"
        />
      )}

      {/* 登出確認框：桌面/手機共用 */}
      {showLogoutConfirm && (
        <ConfirmDialog
          open={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onSuccess={() => {
            setShowLogoutConfirm(false);
            handleLogout();
          }}
          title="確定要登出嗎？"
          message="登出後需重新登入才能使用會員功能"
          confirmTxt="確定登出"
          image="/images/ic-personal.png"
        />
      )}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div className="dashboard">
          <nav className={` ${showNav ? "show" : "hide"}`}>
            <div className="main-nav">
              <div className="logo">
                <Link to="/">
                  <img
                    src={`/images/ic-yuanchen-logo.png`}
                    loading="lazy"
                    alt="元辰雲閣"
                  />
                </Link>
              </div>

              {/* 上方選單功能 */}
              <div className="nav-list">
                {/* 命理服務功能 */}
                {showServiceMenu && (
                  <div className="outer-fortune-service">
                    <div className="fortune-service">
                      <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 0 }}>
                          <img
                            src={`/images/ic-flow-year.png`}
                            loading="lazy"
                            alt="流年運勢詳批"
                          />
                          <p>流年運勢詳批</p>
                        </NavLink>
                      </div>

                      {/* <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 1 }}>
                          <img
                            src={`/images/ic-big-flow.png`}
                            loading="lazy"
                            alt="十年大運詳批"
                          />
                          <p>十年大運詳批</p>
                        </NavLink>
                      </div> */}

                      <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 1 }}>
                          <img
                            src={`/images/ic-family-report.png`}
                            loading="lazy"
                            alt="親子合盤"
                          />
                          <p>親子合盤</p>
                        </NavLink>
                      </div>

                      <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 2 }}>
                          <img
                            src={`/images/ic-love-report.png`}
                            loading="lazy"
                            alt="真愛合盤"
                          />
                          <p>真愛合盤</p>
                        </NavLink>
                      </div>

                      {/* <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 3 }}>
                          <img
                            src={`/images/ic-friends-report.png`}
                            loading="lazy"
                            alt="友情合盤"
                          />
                          <p>友情合盤</p>
                        </NavLink>
                      </div> */}

                      {/* <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 4 }}>
                          <img
                            src={`/images/ic-name-report.png`}
                            loading="lazy"
                            alt="姓名分析"
                          />
                          <p>姓名分析</p>
                        </NavLink>
                      </div> */}

                      <div className="item-service">
                        <NavLink to="/fortune-service" state={{ index: 3 }}>
                          <img
                            src={`/images/ic-trigrams.png`}
                            loading="lazy"
                            alt="奇門遁甲"
                          />
                          <p>奇門遁甲</p>
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}

                {/* 主選單功能 */}
                <ul>
                  {/* aAibo App 入口（/aaibo 獨立 landing，開新分頁）
                      這一版先不露出，要上線時把下面整塊取消註解即可；
                      對應樣式 li.app-download 仍留在 nav-style.scss
                  <li
                    className="nav-item app-download"
                    onMouseEnter={() => {
                      setShowServiceMenu(false);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <a
                      className="nav-link"
                      href="/aaibo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="app-star"
                        src="/images/ic-super-star.svg"
                        alt=""
                      />
                      App 下載
                    </a>
                  </li>
                  */}

                  <li
                    className="nav-item"
                    onMouseEnter={() => {
                      setShowServiceMenu(false);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <NavLink
                      to="/story"
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      品牌故事
                    </NavLink>
                  </li>

                  <li
                    className="nav-item"
                    onMouseEnter={() => {
                      setShowServiceMenu(false);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <NavLink
                      to="/personal-chart"
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                      onClick={handlePersonalChartClick}
                    >
                      個人專屬命盤
                    </NavLink>
                  </li>

                  <li
                    className="nav-item service"
                    onMouseEnter={() => {
                      setShowServiceMenu(true);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <NavLink
                      to="/fortune-service"
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      命理服務
                    </NavLink>
                    {showServiceMenu && <div className="triangle"></div>}
                  </li>

                  <li
                    className="nav-item"
                    onMouseEnter={() => {
                      setShowServiceMenu(false);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <NavLink
                      to="/library"
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      雲閣藏書
                    </NavLink>
                  </li>
                  {/* <li
                    className="nav-item"
                    onMouseEnter={() => {
                      setShowServiceMenu(false);
                      setShowPersonalMenu(false);
                    }}
                  >
                    <NavLink
                      to="/store"
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      雲上雜貨店
                    </NavLink>
                  </li> */}
                </ul>

                {/*登入登出海關*/}
                <div className="login-customs">
                  {!isLogin && (
                    <button
                      onClick={() => {
                        setShowLoginDialog(true);
                      }}
                      onMouseEnter={() => setShowServiceMenu(false)}
                    >
                      登入
                    </button>
                  )}

                  {isLogin && (
                    <div
                      className="user-photo"
                      onMouseEnter={() => {
                        setShowServiceMenu(false);
                        setShowPersonalMenu(true);
                      }}
                    >
                      <img
                        src={
                          user?.userPhoto || "/images/ic-default-userphoto.png"
                        }
                        alt="user photo"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          if (
                            !e.target.src.endsWith(
                              "/images/ic-default-userphoto.png",
                            )
                          ) {
                            e.target.src = "/images/ic-default-userphoto.png";
                          }
                        }}
                      />

                      {showPersonalMenu && (
                        <div className="personal-triangle"></div>
                      )}
                    </div>
                  )}
                </div>

                {/*個人檔案*/}
                {showPersonalMenu && (
                  <div className="outer-personal-menu">
                    <div className="personal-menu">
                      <p className="static-txt">
                        {user?.userName || "元辰會員"}
                      </p>
                      <p className="static-txt">
                        {user?.userEmail || "會員信箱"}
                      </p>
                      <div className="my-point">
                        <p className="static-txt">擁有</p>
                        <img src="/images/ic-yellow-apoint.png"></img>
                        <p className="static-txt">{user?.userAPoint}</p>
                      </div>
                      <div className="h-line"></div>
                      <div className="menu-item">
                        <Link
                          className="nav-link"
                          to="/personal-board"
                          onClick={() => setShowPersonalMenu(false)}
                        >
                          <p>個人檔案</p>
                        </Link>
                      </div>
                      <div className="menu-item">
                        <Link
                          className="nav-link"
                          to="/ask-teacher"
                          onClick={() => setShowPersonalMenu(false)}
                        >
                          <p>
                            <span className="menu-label">
                              問問老師
                              {unreadAsk && (
                                <img
                                  className="nav-noti"
                                  src="/images/ic-notification.png"
                                  alt=""
                                />
                              )}
                            </span>
                          </p>
                        </Link>
                      </div>
                      <div className="menu-item">
                        <Link
                          className="nav-link"
                          to="/study-room"
                          onClick={() => setShowPersonalMenu(false)}
                        >
                          <p>
                            <span className="menu-label">
                              我的書房
                              {unreadStudy && (
                                <img
                                  className="nav-noti"
                                  src="/images/ic-notification.png"
                                  alt=""
                                />
                              )}
                            </span>
                          </p>
                        </Link>
                      </div>
                      <div className="menu-item">
                        <Link
                          className="nav-link"
                          to="/point"
                          onClick={() => setShowPersonalMenu(false)}
                        >
                          <p>點數管理</p>
                        </Link>
                      </div>
                      <div className="menu-item">
                        <Link
                          className="nav-link"
                          to="/purchase-record"
                          onClick={() => setShowPersonalMenu(false)}
                        >
                          <p>購買記錄</p>
                        </Link>
                      </div>
                      <div className="menu-item">
                        <p onClick={() => setShowPersonalMenu(false)}>
                          官方LINE客服
                        </p>
                      </div>

                      <div className="menu-item">
                        <p onClick={openLogoutConfirm}>登出</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* 移動裝置佈局 */}
      {isMobile && (
        <div className="mobile-dashboard">
          <nav className={` ${showNav ? "show" : "hide"}`}>
            <img
              className="menu"
              src={`/images/ic-menu.png`}
              loading="lazy"
              onClick={() => setIsOpen(true)}
            />

            <Link className="logo-link" to="/">
              <img
                className="logo"
                src={`/images/ic-yuanchen-logo.png`}
                loading="lazy"
                alt="元辰雲閣"
              />
            </Link>

            {/*登入登出海關*/}
            <div className="login-customs">
              {!isLogin && (
                <button
                  onClick={() => {
                    setShowLoginDialog(true);
                  }}
                  onMouseEnter={() => setShowServiceMenu(false)}
                >
                  登入
                </button>
              )}

              {isLogin && (
                <div
                  className="user-photo"
                  onClick={() => {
                    setShowPersonalMenu(!showPersonalMenu);
                  }}
                >
                  <img
                    src={user?.userPhoto || "/images/ic-default-userphoto.png"}
                    alt="user photo"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      if (
                        !e.target.src.endsWith(
                          "/images/ic-default-userphoto.png",
                        )
                      ) {
                        e.target.src = "/images/ic-default-userphoto.png";
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </nav>

          {/*個人檔案浮動選單*/}
          {showPersonalMenu && (
            <div
              className="mobile-personal-menu-overlay"
              onClick={() => setShowPersonalMenu(false)}
            >
              <div
                className="mobile-personal-menu"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="menu-item">
                  <Link
                    className="nav-link"
                    to="/personal-board"
                    onClick={() => setShowPersonalMenu(false)}
                  >
                    個人檔案
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="nav-link"
                    to="/ask-teacher"
                    onClick={() => setShowPersonalMenu(false)}
                  >
                    <span className="menu-label">
                      問問老師
                      {unreadAsk && (
                        <img
                          className="nav-noti"
                          src="/images/ic-notification.png"
                          alt=""
                        />
                      )}
                    </span>
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="nav-link"
                    to="/study-room"
                    onClick={() => setShowPersonalMenu(false)}
                  >
                    <span className="menu-label">
                      我的書房
                      {unreadStudy && (
                        <img
                          className="nav-noti"
                          src="/images/ic-notification.png"
                          alt=""
                        />
                      )}
                    </span>
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="nav-link"
                    to="/point"
                    onClick={() => setShowPersonalMenu(false)}
                  >
                    點數管理
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="nav-link"
                    to="/purchase-record"
                    onClick={() => setShowPersonalMenu(false)}
                  >
                    購買記錄
                  </Link>
                </div>
                <div className="menu-item">
                  <p>官方LINE客服</p>
                </div>
                <div className="menu-item last">
                  <p onClick={openLogoutConfirm}>登出</p>
                </div>
              </div>
            </div>
          )}

          {/*側邊抽屜*/}
          <div className={`drawer ${isOpen ? "open" : ""}`}>
            <div className="drawer-left">
              <ul className="menu-list">
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    to="/story"
                    onClick={() => setIsOpen(false)}
                  >
                    品牌故事
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    to="/personal-chart"
                    onClick={(e) => {
                      setIsOpen(false);
                      handlePersonalChartClick(e);
                    }}
                  >
                    個人專屬命盤
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    to="/fortune-service"
                    onClick={() => setIsOpen(false)}
                  >
                    命理服務
                  </NavLink>
                  <div className="service-submenu">
                    <div className="v-line"></div>
                    <ul className="submenu">
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 0 }}
                          onClick={() => setIsOpen(false)}
                        >
                          流年運勢詳批
                        </NavLink>
                      </li>
                      {/* <div className="space"></div> */}
                      {/* <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 1 }}
                          onClick={() => setIsOpen(false)}
                        >
                          十年大運詳批
                        </NavLink>
                      </li> */}
                      <div className="space"></div>
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 1 }}
                          onClick={() => setIsOpen(false)}
                        >
                          親子合盤
                        </NavLink>
                      </li>
                      <div className="space"></div>
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 2 }}
                          onClick={() => setIsOpen(false)}
                        >
                          真愛合盤
                        </NavLink>
                      </li>
                      <div className="space"></div>
                      {/* <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 3 }}
                          onClick={() => setIsOpen(false)}
                        >
                          友情合盤
                        </NavLink>
                      </li> */}
                      {/* <div className="space"></div> */}
                      {/* <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 4 }}
                          onClick={() => setIsOpen(false)}
                        >
                          姓名分析測算
                        </NavLink>
                      </li> */}
                      {/* <div className="space"></div> */}
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `nav-link ${isActive ? "active" : ""}`
                          }
                          to="/fortune-service"
                          state={{ index: 3 }}
                          onClick={() => setIsOpen(false)}
                        >
                          奇門遁甲
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    to="/library"
                    onClick={() => setIsOpen(false)}
                  >
                    雲閣藏書
                  </NavLink>
                </li>
                {/* aAibo App 入口（/aaibo 獨立 landing，開新分頁）
                    這一版先不露出，要上線時把下面整塊取消註解即可
                <li className="app-download">
                  <a
                    className="nav-link"
                    href="/aaibo"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                  >
                    <img
                      className="app-star"
                      src="/images/ic-super-star.svg"
                      alt=""
                    />
                    App 下載
                  </a>
                </li>
                */}
                {/* <li>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    to="/store"
                    onClick={() => setIsOpen(false)}
                  >
                    雲上雜貨店
                  </NavLink>
                </li> */}
              </ul>
            </div>

            <div className="drawer-right">
              <i
                className="bi bi-x close-button"
                onClick={() => setIsOpen(false)}
              ></i>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavListComponent;
